import { CommonModule, DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { UiServicesLogger, UiServicesUtils, UiServiceWebSession } from '@drct-web-apps/ui-services';
import { DRCTTestimonies, DRCTTestimonyView } from '@drct-web-apps/ui-types';
import { UiWebPageTitleComponent } from '@drct-web-apps/ui-web-page-title';
import { ReplaySubject, takeUntil } from 'rxjs';



@Component({
  selector: 'ui-web-testimonies',
  templateUrl: './ui-web-testimonies.component.html',
  imports: [ UiWebPageTitleComponent, CommonModule ]
})
export class UiWebTestimoniesComponent implements OnInit, OnDestroy {
  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Input() showPageTitle = true;
  @Input() showAllTestimonies = true;

  public currentPage = 1;
  public totalPages = 1;
  private readonly testimoniesPerPage = 9;
  private readonly shortMsgLength = 150;

  public showPagination = false;
  public testimonies: DRCTTestimonies[] = [];
  public testimoniesToShow: DRCTTestimonies[] = [];
  public buttonState: boolean[] = [];

  constructor(private readonly http: HttpClient, private readonly utils: UiServicesUtils,
              @Inject(DOCUMENT) private readonly document: Document, private readonly logger: UiServicesLogger,
              private readonly session: UiServiceWebSession) { }

  ngOnInit(): void {
    this.currentPage = 1;
    this.testimonies = [];
    this.testimoniesToShow = [];
    this.buttonState = [];
    this.showPagination = this.showAllTestimonies;
    
    if(this.session.initComplete) {
      this.loadAllTestimonies();
    }
    else {
      this.session.sessionReady
                  .pipe(takeUntil(this.destroyed$))
                  .subscribe((ready: boolean) => {
                    if(ready) {
                      this.loadAllTestimonies();
                    }
                  });  
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public onPaginate(page: string): void {
    switch(page) {
      case "F":
        this.currentPage = 1;
        break;
      case "L":
        this.currentPage = this.totalPages;
        break;
      case "P":
        this.currentPage = (this.currentPage > 1)? this.currentPage - 1: this.currentPage;
        break;
      case "N":
        this.currentPage = (this.currentPage < this.totalPages)? this.currentPage + 1: this.currentPage;
        break;
    }

    this.paginateTestimonies();
  }

  public getShortMessage(message: string): string {
    return message.length > this.shortMsgLength? message.substring(0, this.shortMsgLength) + "...": message;
  }

  public onReadMoreClick(index: number, message: string): void {
    const element = this.document.getElementById(`message-${index}`);
    if (element) {
      element.innerHTML = message;
    }
    this.buttonState[index] = false;
  }

  public onReadLessClick(index: number, message: string): void {
    const element = this.document.getElementById(`message-${index}`);
    if (element) {
      element.innerHTML = this.getShortMessage(message);
    }
    this.buttonState[index] = true;
  }

  public showReadMoreButton(message: string): boolean {
    return message.length > this.shortMsgLength;
  }

  public disableFirstPrevButton(): boolean {
    return this.currentPage === 1;
  }

  public disableNextLastButton(): boolean {
    return this.currentPage === this.totalPages;
  }


  private loadAllTestimonies(): void {
      const formData = new FormData();
      formData.append("frmData", JSON.stringify({ opr: "view-testimonies", viewAll: "N", uuid: (this.session.webConfig?.uuid ?? '') }));
  
      this.http.post(this.utils.builApiUrlWithEnv("drct_forms.php"), formData)
               .pipe(takeUntil(this.destroyed$))
               .subscribe({
                  next:(response: DRCTTestimonyView) => {
                    if(response.success && response.tot_rows &&  response.tot_rows > 0) {
                      this.testimonies = response.testimonies ?? [];
                      this.totalPages = Math.ceil(this.testimonies.length / this.testimoniesPerPage);
                      this.paginateTestimonies();
                    }
                  },
                  error: (error) => {
                    this.logger.error("Error loading testimonies. Error: " + error);   
                  }
                });
    }

    private paginateTestimonies(): void {
      if(!this.showAllTestimonies) {
        this.testimoniesToShow = this.testimonies.slice(0, 3);
        this.buttonState = new Array(this.testimoniesToShow.length).fill(true);
        return;
      }

      this.testimoniesToShow = this.testimonies.slice((this.currentPage - 1) * this.testimoniesPerPage, this.currentPage * this.testimoniesPerPage);
      this.buttonState = new Array(this.testimoniesToShow.length).fill(true);
    }
}
